import React, { useMemo } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import moment from "moment";
import koLocale from "@fullcalendar/core/locales/ko";
import styles from "./CusFullCalendar.module.css";

type HolidayEntry = {
    holiday?: string;
    holidayDate?: string;
    date?: string;
    memo?: string;
    holidayRemark?: string;
    title?: string;
    holidayType?: string;
    regionId?: string;
};

type CusFullCalendarProps = {
    mode?: "U" | "R";
    holidayList?: HolidayEntry[];
    dateClick?: (event: any, dateStr: string) => void;
};

const normalizeHolidayEntry = (entry: HolidayEntry) => {
    const dateValue = entry?.holiday || entry?.holidayDate || entry?.date;
    if (!dateValue) return null;

    const normalizedDate = moment(dateValue).format("YYYY-MM-DD");
    const title = entry?.memo || entry?.holidayRemark || entry?.title || "";

    return {
        date: normalizedDate,
        title,
        meta: entry,
    };
};

function CusFullCalendar({ mode = "R", holidayList = [], dateClick }: CusFullCalendarProps) {
    const normalizedEvents = useMemo(() => {
        return (holidayList ?? [])
            .map((entry) => normalizeHolidayEntry(entry))
            .filter((entry): entry is NonNullable<ReturnType<typeof normalizeHolidayEntry>> => Boolean(entry));
    }, [holidayList]);

    const holidayDates = useMemo(() => new Set(normalizedEvents.map((event) => event.date)), [normalizedEvents]);

    const dayCellContent = (arg: any) => {
        const dayNumberText = arg.dayNumberText || "";
        const dayNumber = typeof dayNumberText === "string" ? dayNumberText.replace(/\D/g, "") : arg.dayNumber;
        const dateText = moment(arg.date).format("YYYY-MM-DD");
        const todayText = moment().format("YYYY-MM-DD");
        const isToday = dateText === todayText;
        const dayOfWeek = moment(arg.date).day();
        const isSunday = dayOfWeek === 0;
        const isSaturday = dayOfWeek === 6;
        const isHoliday = holidayList.map((ele) => ele.holiday).includes(dateText);

        // 일요일/공휴일은 빨간색, 토요일은 파란색
        const dayColor = isSunday ? "red" : isSaturday ? "blue" : isHoliday ? "red" : undefined;
        return (
            <div
                style={{
                    textAlign: "left",
                    color: isToday ? undefined : dayColor,
                }}
            >
                <span className={`fc-daygrid-day-top fc-daygrid-day-number ${isToday ? styles["today-day-number-circle-text"] : ""}`}>
                    {dayNumber || arg.dayNumber}
                    {isToday && <span className={styles["today-day-number-circle"]}></span>}
                    
                </span>
            </div>
        );
    };

    function dayCellClassNames(arg: any) {
        const dateText = moment(arg.date).format('YYYY-MM-DD');
        if (holidayList.map(ele => ele.holiday).includes(dateText)) {
            return 'holiday';
        } else {
            return '';
        }
    }

    function eventContent(arg: any) {
        const eventData = arg.event;
        const holidayData = arg.event._def.extendedProps.meta;
        const isKoreaHoliday = holidayData?.holidayType === "HT001";
        if(!eventData.title) {
            return null;
        }
        return (
            <div
                onClick={(event) => {
                    if (mode !== "U") return;
                    const date = moment(eventData.start).format("YYYY-MM-DD");
                    dateClick(event, date);
                }}
                className={`event_content ${isKoreaHoliday ? "holiday_korea" : "holiday_region"} ${
                    holidayData?.regionId ? `holiday_${holidayData.regionId}` : ""
                }`}
            >
                {eventData.title}
            </div>
        );
    }

    return (
        <div style={{ position: "relative" }}>
            <FullCalendar
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                locale={koLocale}
                height={"auto"}
                titleFormat={{ year: "numeric", month: "2-digit" }}
                headerToolbar={{
                    start: "today prev title next",
                    end: "",
                }}
                dateClick={(arg) => {
                    if (mode !== "U" || !dateClick) return;
                    dateClick(arg.jsEvent, arg.dateStr);
                }}
                eventContent={eventContent}
                dayCellClassNames={dayCellClassNames}
                dayCellContent={dayCellContent}
                events={normalizedEvents}
            />
        </div>
    );
}

export default CusFullCalendar;
